import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import BasicField from '@payaca/components/basicField/BasicField';
import JobLineItemCard from '../jobLineItemCard/JobLineItemCard';

import { requestGetDeal } from '@payaca/store/deals/dealsActions';
import {
  useAcceptedAndDraftJobLineItemAndAmendments,
  useChangeProposal,
  useJobLineItem,
} from '@payaca/store/hooks/appState';
import {
  clearJobLineItem,
  createJobLineItemFromAmendmentParent,
  requestDeleteJobLineItem,
  requestGetJobLineItem,
  requestUpdateJobLineItem,
} from '@payaca/store/jobContent/jobContentActions';
import { UpdateJobLineItemRequestData } from '@payaca/store/jobContent/jobContentTypes';
import { requestGetChangeProposal } from '@payaca/store/proposals/proposalsActions';

type Props = {
  jobLineItemId: number;
  changeProposalId: number;
};

const AcceptedJobLineItemEditControl: FC<Props> = ({
  jobLineItemId,
  changeProposalId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  const jobLineItem = useJobLineItem(jobLineItemId);
  const changeProposal = useChangeProposal(changeProposalId);

  const jobLineItemAndAmendments = useAcceptedAndDraftJobLineItemAndAmendments(
    jobLineItemId,
    changeProposalId
  );
  const [quantityAmendment, setQuantityAmendment] = useState(0);
  const [requiresPersist, setRequiresPersist] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const currentAmendmentJobLineItem = useMemo(() => {
    return jobLineItemAndAmendments.find((x) => {
      return x.changeProposalId === changeProposalId;
    });
  }, [jobLineItemAndAmendments, changeProposalId]);

  const acceptedCumulativeQuantity = useMemo(() => {
    return jobLineItemAndAmendments.reduce((acc, jli) => {
      if (jli.changeProposalId === changeProposalId) return acc;
      return acc + jli.quantity;
    }, 0);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'changeProposalId'
  }, [jobLineItemAndAmendments]);

  const cumulativeQuantity = useMemo(() => {
    return jobLineItemAndAmendments.reduce((acc, jli) => {
      return acc + jli.quantity;
    }, 0);
  }, [jobLineItemAndAmendments]);

  useEffect(() => {
    if (currentAmendmentJobLineItem) {
      setQuantityAmendment(currentAmendmentJobLineItem.quantity);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'currentAmendmentJobLineItem.quantity', and 'currentAmendmentJobLineItem'
  }, [currentAmendmentJobLineItem?.id]);

  useEffect(() => {
    if (!isProcessing) {
      setQuantityAmendment(currentAmendmentJobLineItem?.quantity || 0);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'isProcessing'
  }, [currentAmendmentJobLineItem?.quantity]);

  const handlePersist = useCallback(() => {
    if (quantityAmendment === 0) {
      if (currentAmendmentJobLineItem) {
        setIsProcessing(true);
        dispatch(
          requestDeleteJobLineItem(currentAmendmentJobLineItem.id, () => {
            setIsProcessing(false);
            dispatch(requestGetChangeProposal(changeProposalId));
            dispatch(clearJobLineItem(currentAmendmentJobLineItem.id));
            if (changeProposal) {
              dispatch(requestGetDeal(changeProposal.dealId));
            }
          })
        );
      }
    } else if (currentAmendmentJobLineItem) {
      setIsProcessing(true);
      dispatch(
        requestUpdateJobLineItem(
          {
            jobLineItemId: currentAmendmentJobLineItem.id,
            quantity: quantityAmendment,
            description: currentAmendmentJobLineItem.description,
            reference: currentAmendmentJobLineItem.name || undefined,
            price: currentAmendmentJobLineItem.price,
            materialCostMarkupPercentage:
              currentAmendmentJobLineItem.materialCostMarkupPercentage,
            materialCostMarkupAmount:
              currentAmendmentJobLineItem.materialCostMarkupAmount,
            taxRateId: currentAmendmentJobLineItem.taxRateId,
            cisApplies: !!currentAmendmentJobLineItem.cisDeductionRate,
            lineItemId: currentAmendmentJobLineItem.lineItemId,
          } as UpdateJobLineItemRequestData,
          () => {
            dispatch(
              requestGetJobLineItem(currentAmendmentJobLineItem.id, () =>
                setIsProcessing(false)
              )
            );
            dispatch(requestGetChangeProposal(changeProposalId));
            setIsProcessing(false);
          }
        )
      );
    } else {
      setIsProcessing(true);
      dispatch(
        createJobLineItemFromAmendmentParent.request({
          data: {
            changeProposalId: changeProposalId,
            quantity: quantityAmendment,
            amendmentParentJobLineItemId: jobLineItemId,
          },
          callback: (jliId: number) => {
            setIsProcessing(false);
            dispatch(requestGetJobLineItem(jliId));
            dispatch(requestGetChangeProposal(changeProposalId));
            if (changeProposal) {
              dispatch(requestGetDeal(changeProposal.dealId));
            }
          },
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'currentAmendmentJobLineItem.materialCostMarkupPercentage', 'currentAmendmentJobLineItem.description', 'changeProposal', 'currentAmendmentJobLineItem.materialCostMarkupAmount', 'dispatch', 'currentAmendmentJobLineItem', 'currentAmendmentJobLineItem.name', 'currentAmendmentJobLineItem.taxRateId',
  }, [
    currentAmendmentJobLineItem?.id,
    quantityAmendment,
    changeProposalId,
    jobLineItemId,
    changeProposal?.dealId,
  ]);

  useEffect(() => {
    if (requiresPersist && !isProcessing) {
      setRequiresPersist(false);
      handlePersist();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'handlePersist'
  }, [requiresPersist, isProcessing]);

  if (!jobLineItem) return null;

  return (
    <div>
      <JobLineItemCard
        jobLineItem={{ ...jobLineItem, quantity: cumulativeQuantity }}
        quantitySectionComponent={
          <div>
            <span>Qty</span>
            <BasicField
              // need to keep this element uncontrolled by passing defaultValue rather than value so that it will work in safari and ff
              // value={acceptedCumulativeQuantity + quantityAmendment}
              name="quantity"
              type="number"
              onChange={(value) => {
                if (value.quantity?.length) {
                  setQuantityAmendment(
                    Number(value.quantity) - acceptedCumulativeQuantity
                  );
                }
              }}
              additionalInputProps={{
                defaultValue:
                  acceptedCumulativeQuantity +
                  (currentAmendmentJobLineItem?.quantity || 0),
                step: 1,
              }}
              onChangeTimeout={() => setRequiresPersist(true)}
            />
          </div>
        }
      />
    </div>
  );
};
export default AcceptedJobLineItemEditControl;
